import { useState, useMemo } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import { Modal } from './modal';
import { SYMBOL_CATEGORIES, DERIV_CATEGORIES } from '@/lib/symbol-categories';

interface SymbolPickerProps {
  value: string;
  onChange: (symbol: string) => void;
  placeholder?: string;
  error?: string;
  className?: string;
}

export function SymbolPicker({
  value,
  onChange,
  placeholder = 'Select a symbol...',
  error,
  className,
}: SymbolPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return SYMBOL_CATEGORIES;
    const q = search.trim().toLowerCase();
    const result: Record<string, string[]> = {};
    for (const [cat, symbols] of Object.entries(SYMBOL_CATEGORIES)) {
      const isDeriv = DERIV_CATEGORIES.has(cat);
      const matched = symbols.filter(
        (s) =>
          s.toLowerCase().includes(q) ||
          cat.toLowerCase().includes(q) ||
          (isDeriv && 'deriv'.includes(q)),
      );
      if (matched.length > 0) result[cat] = matched;
    }
    return result;
  }, [search]);

  const handleSelect = (symbol: string) => {
    onChange(symbol);
    setOpen(false);
    setSearch('');
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`w-full bg-surface border rounded-lg px-3 py-2 text-sm font-mono text-left outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)] cursor-pointer flex items-center justify-between ${
          error ? 'border-red' : 'border-glass-border'
        } ${className ?? ''}`}
      >
        <span className={value ? 'text-ink' : 'text-ink-4'}>
          {value || placeholder}
        </span>
        <ChevronDown size={14} className="text-ink-3 shrink-0" />
      </button>
      {error && <p className="text-[10px] text-red mt-1">{error}</p>}

      <Modal open={open} onClose={() => { setOpen(false); setSearch(''); }} title="Select Symbol" width="max-w-lg">
        {/* Search */}
        <div className="relative mb-3">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search symbols..."
            autoFocus
            className="w-full bg-surface border border-glass-border rounded-lg pl-9 pr-3 py-2 text-sm text-ink outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)] placeholder:text-ink-4"
          />
        </div>

        {/* Categories */}
        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
          {Object.entries(filteredCategories).map(([category, symbols]) => {
            const isDeriv = DERIV_CATEGORIES.has(category);
            return (
            <div key={category}>
              <h3 className="text-[10px] font-extrabold text-ink-3 uppercase tracking-wider mb-1.5 px-1 flex items-center gap-1.5">
                {category}
                {isDeriv && (
                  <span className="text-[8px] font-bold px-1.5 py-0.5 rounded normal-case tracking-normal" style={{ backgroundColor: 'rgba(237, 72, 71, 0.15)', color: '#ED4847' }}>
                    Deriv
                  </span>
                )}
              </h3>
              <div className="grid grid-cols-2 gap-1">
                {symbols.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleSelect(s)}
                    className={`text-left text-xs font-mono px-3 py-2 rounded-lg transition-all cursor-pointer ${
                      s === value
                        ? 'bg-orange-bg text-orange border border-orange/20'
                        : 'bg-surface text-ink border border-glass-border hover:bg-surface-2 hover:border-orange/30'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            );
          })}
          {Object.keys(filteredCategories).length === 0 && (
            <p className="text-xs text-ink-3 text-center py-4">No symbols match "{search}"</p>
          )}
        </div>
      </Modal>
    </>
  );
}
